import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import { FileMetadata } from 'src/app/models/collection.interface';
import { Persona } from 'src/app/models/personas.interface';

const MOCK_FILES: FileMetadata[] = [
  {
    catalogue: { company: 'Acme', state: 'NY', year: '2023', title: 'Q4 Report', dimensionAttribute: 'Value' },
    collection_id: 'coll-001',
    file_id: 'file-a1',
    processing_stage: 'complete',
    original_document_uri: 's3://original/doc-a1.pdf',
    s3_output_path: 's3://processed/doc-a1.json'
  },
  {
    catalogue: { company: 'BetaCorp', state: 'CA', year: '2024', title: 'RFP Response', dimensionAttribute: 'Value' },
    collection_id: 'coll-002',
    file_id: 'file-b2',
    processing_stage: 'text_extraction',
    original_document_uri: 's3://original/doc-b2.pdf',
    s3_output_path: 's3://processed/doc-b2.json'
  }
];

const MOCK_PERSONAS: Persona[] = [
  {
    id: 'sales-analyst-v1',
    name: 'System Default',
    summary: 'The core factual assistant persona.',
    description: 'Provides concise, factual, and neutral answers based strictly on source material.',
    exampleDocuments: MOCK_FILES.slice(0, 1),
    is_hidden: false
  },
  {
    id: 'p-custom',
    name: 'Marketing Assistant',
    summary: 'Persona for generating marketing copy.',
    description: 'Always uses a highly persuasive and positive tone.',
    exampleDocuments: MOCK_FILES,
    is_hidden: true
  }
];

@Injectable()
export class MockPersonaService {
  private personasStore: Persona[] = [...MOCK_PERSONAS];

  config = { driftApi: '/mock-api/v1' };

  FILE_TYPE_PDF = '.pdf';
  FILE_TYPE_TXT = '.txt';

  getPersonas(includeHidden = false): Observable<Persona[]> {
    const data = includeHidden ? this.personasStore : this.personasStore.filter(p => !p.is_hidden);
    return of(data).pipe(delay(2000));
  }

  getPersona(id: string): Observable<Persona> {
    const persona = this.personasStore.find(p => p.id === id);
    if (persona) {
      return of(persona).pipe(delay(2000));
    }
    return throwError(() => new Error(`Persona ${id} not found`));
  }

  personaLog(persona: Persona): Observable<{ filename: string }> {
    return of({ filename: `${persona.id}-log-12345.txt` }).pipe(delay(2000));
  }

  generatePersona(persona: Persona): Observable<{ description: string }> {
    console.log(`Mocking generatePersona for ID: ${persona.id}`);
    return of({ description: 'Mock generated detailed description for a marketing persona.' }).pipe(delay(2000));
  }

  updatePersona(persona: Persona): Observable<Persona> {
    const index = this.personasStore.findIndex(p => p.id === persona.id);
    if (index > -1) {
      this.personasStore[index] = { ...this.personasStore[index], ...persona };
      console.log(`Mocking updatePersona: ${persona.id} updated`);
      return of(this.personasStore[index]).pipe(delay(2000));
    }
    return throwError(() => new Error(`Update failed: Persona ${persona.id} not found`));
  }

  createPersona(persona: Persona): Observable<Persona> {
    const newPersona: Persona = {
      ...persona,
      id: `p-${Math.random().toString(36).substring(2, 9)}`,
      is_hidden: persona.is_hidden ?? false
    };
    this.personasStore.push(newPersona);
    console.log(`Mocking createPersona: ${newPersona.id} created`);
    return of(newPersona).pipe(delay(2000));
  }

  deletePersona(persona: Persona): Observable<Persona> {
    const initialLength = this.personasStore.length;
    this.personasStore = this.personasStore.filter(p => p.id !== persona.id);
    if (this.personasStore.length < initialLength) {
      console.log(`Mocking deletePersona: ${persona.id} deleted`);
      return of(persona).pipe(delay(2000));
    }
    return throwError(() => new Error(`Delete failed: Persona ${persona.id} not found`));
  }

  deleteUploadedDocument(personaId: string, fileName: string): Observable<object> {
    console.log(`Mocking deleteUploadedDocument: Persona ${personaId}, File: ${fileName}`);
    return of({ message: 'File deleted' }).pipe(delay(2000));
  }

  convertPDFtoTxtAndUpload(id: string, files: File[], directory: string): Observable<object> {
    console.log(`Mocking convertPDFtoTxtAndUpload for ${files.length} files to ${directory}/${id}`);
    const mockResponses = files.map(file => ({ type: 4, file: file.name, status: 200 }));
    return of(mockResponses).pipe(delay(200));
  }
}
